import type {
  ArticleThreadStarterPublisher,
  Campaign,
  Group,
  GroupThreadStarterPublisher,
  LiveEvent,
  Message,
  Publisher,
  TeamsMeeting,
  User,
  YammerState,
} from '../../state/types';

import { findByIdThrows as findPublisherByIdThrows } from './findPublisherSelectors';

export const directMessagePublisherId: string = btoa('direct');
export const homePublisherId: string = btoa('home');
export const leaderPublisherId: string = btoa('leader');
export const shareToPublisherId: string = btoa('shareTo');
export const networkQuestionPublisherId: string = btoa('network-question');
export const articlePublisherId: string = btoa('article');
export const universalCreatePublisherId: string = btoa('universal-create');
export const dashboardCreatePublisherId: string = btoa('dashboard-create');

type GetCampaignAnalyticsPublisherId = (campaignId: Campaign['id']) => Publisher['id'];
export const getCampaignAnalyticsPublisherId: GetCampaignAnalyticsPublisherId = (campaignId) =>
  btoa(`campaign-analytics-${campaignId}`);

type GetNetworkAnalyticsPublisherId = (userId: User['id']) => Publisher['id'];
export const getNetworkAnalyticsPublisherId: GetNetworkAnalyticsPublisherId = (userId) =>
  btoa(`network-analytics-${userId}`);

type GetAudienceAnalyticsPublisherId = (userId: User['id']) => Publisher['id'];
export const getAudienceAnalyticsPublisherId: GetAudienceAnalyticsPublisherId = (userId) =>
  btoa(`audience-analytics-${userId}`);

type GetCampaignPublisherId = (campaignId: Campaign['id']) => Publisher['id'];
export const getCampaignPublisherId: GetCampaignPublisherId = (campaignId) => btoa(`campaign-${campaignId}`);

type GetGroupAnalyticsPublisherId = (groupId: Group['id']) => Publisher['id'];
export const getGroupAnalyticsPublisherId: GetGroupAnalyticsPublisherId = (groupId) =>
  btoa(`group-analytics-${groupId}`);

type GetEditPublisherId = (messageId: Message['id']) => Publisher['id'];
export const getEditPublisherId: GetEditPublisherId = (messageId) => btoa(`message-${messageId}`);

type GetReplyPublisherId = (threadStarterOrTopLevelMessageId: Message['id']) => Publisher['id'];
export const getReplyPublisherId: GetReplyPublisherId = (threadStarterOrTopLevelMessageId) =>
  btoa(`reply-${threadStarterOrTopLevelMessageId}`);

type GetEventPublisherId = (eventId: LiveEvent['id']) => Publisher['id'];
export const getEventPublisherId: GetEventPublisherId = (eventId) => btoa(`event-${eventId}`);

type GetGroupPublisherId = (groupId: GroupThreadStarterPublisher['groupId']) => Publisher['id'];
export const getGroupPublisherId: GetGroupPublisherId = (groupId) =>
  groupId == null ? homePublisherId : btoa(`group-${groupId}`);

type GetUserPublisherId = (userId: User['id']) => Publisher['id'];
export const getUserPublisherId: GetUserPublisherId = (userId) => btoa(`user-${userId}`);

type GetUserMomentPublisherId = (userId: User['id']) => Publisher['id'];
export const getUserMomentPublisherId: GetUserMomentPublisherId = (userId) => btoa(`user-moment-${userId}`);

type GetShareMessagePublisherId = (messageId: Message['id']) => Publisher['id'];
export const getShareMessagePublisherId: GetShareMessagePublisherId = (messageId) => btoa(`share-message-${messageId}`);

type GetTeamsMeetingPublisherId = (teamsMeetingId: TeamsMeeting['id']) => Publisher['id'];
export const getTeamsMeetingPublisherId: GetTeamsMeetingPublisherId = (teamsMeetingId) =>
  btoa(`teams-meeting-${teamsMeetingId}`);

type GetPremiumEventUpdatesPublisherId = (teamsMeetingId: TeamsMeeting['id']) => Publisher['id'];
export const getPremiumEventUpdatesPublisherId: GetPremiumEventUpdatesPublisherId = (teamsMeetingId) =>
  btoa(`premium-event-updates-${teamsMeetingId}`);

type GetShareTeamsMeetingPublisherId = (teamsMeetingId: TeamsMeeting['id']) => Publisher['id'];
export const getShareTeamsMeetingPublisherId: GetShareTeamsMeetingPublisherId = (teamsMeetingId) =>
  btoa(`share-teams-meeting-${teamsMeetingId}`);

type GetArticlePublisherId = (articleId: ArticleThreadStarterPublisher['articleId']) => Publisher['id'];
export const getArticlePublisherId: GetArticlePublisherId = (articleId) =>
  articleId ? btoa(`article-${articleId}`) : articlePublisherId;

type GetPublisherInstanceIdThrows = (state: YammerState, publisher: Publisher['id']) => string;
export const getPublisherInstanceIdThrows: GetPublisherInstanceIdThrows = (state, publisherId) => {
  const { publisherInstanceId } = findPublisherByIdThrows(state, publisherId);

  return publisherInstanceId;
};

type GetCreatorImpactPublisherId = (userId: User['id']) => Publisher['id'];
export const getCreatorImpactPublisherId: GetCreatorImpactPublisherId = (userId) => btoa(`creator-impact-${userId}`);
